import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eyebrow } from './SectionHead';
import Notice from './Notice';

/**
 * LegalPage — L8 long-form layout with a sticky table of contents (desktop)
 * that collapses to the top on mobile. Sections are `<section id=...>` children.
 */
const LegalPage = ({ id, eyebrow, title, metaBlock, summary, sections, children }) => {
  const { t } = useTranslation('cy');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(sections[0]?.id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);

  return (
    <section className="cy-section cy-zone--doc cy-legal-page" id={id}>
      <div className="cy-container">
        <div className="cy-legal">
          <div className="cy-legal__toc">
            <nav aria-label={t('ui.onThisPage')}>
              <button type="button" className="cy-legal__toc-toggle cy-btn cy-btn--secondary cy-btn--small" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
                {t('ui.onThisPage')}
              </button>
              <ol className={`cy-legal__toc-list${open ? ' is-open' : ''}`}>
                {sections.map((s) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`} className={active === s.id ? 'is-active' : ''} onClick={() => setOpen(false)}>
                      {s.title}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </div>

          <div className="cy-legal__body">
            <header>
              <Eyebrow>{eyebrow}</Eyebrow>
              <h1 className="cy-h1">{title}</h1>
              {metaBlock ? <div className="cy-facts cy-legal__facts">{metaBlock}</div> : null}
              {summary ? <Notice variant="inline" className="cy-legal__summary">{summary}</Notice> : null}
            </header>
            <div className="cy-legal__content">{children}</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LegalPage;
